import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { applyNodeChanges, applyEdgeChanges, addEdge } from '@xyflow/react'
import type { NodeChange, EdgeChange, Connection, Node as RFNode, Edge as RFEdge } from '@xyflow/react'

import type {
  CanvasSnapshot,
  RoadmapNode,
  RoadmapEdge,
  RoadmapArea,
  RoadmapNote,
  RoadmapNodeData,
  BackendNodeType,
  ClientNodeType
} from '@/views/maps/types'

// ============================================================
// TYPES
// ============================================================

/** Грязный/удалённый набор как Record<id,true> — НЕ Set (Immer-нюанс A3). */
type IdSet = Record<string, true>

export interface RoadmapState {
  nodes: RoadmapNode[]
  edges: RoadmapEdge[]
  areas: RoadmapArea[]
  notes: RoadmapNote[]
  selectedId: string | null
  selectedKind: 'node' | 'edge' | 'area' | 'note' | null
  dirty: { nodes: IdSet; edges: IdSet; areas: IdSet; notes: IdSet }
  deleted: { nodeIds: IdSet; edgeIds: IdSet; areaIds: IdSet; noteIds: IdSet }
  past: CanvasSnapshot[]
  future: CanvasSnapshot[]
  perfWarningShown: boolean
  saveStatus: 'idle' | 'saving' | 'saved' | 'error'
  /** Клиентский режим карты (LOCK-01): view (по умолчанию) или edit (держим лок). */
  mode: 'view' | 'edit'
  /**
   * Снимок активного лока редактирования (из status-poll / heartbeat-ответа).
   * Даты — строки (приходят сериализованными из JSON-роута). null = лок свободен.
   */
  lock: {
    holderId: string
    holderName: string | null
    sessionId: string
    takeoverRequestedById: string | null
    takeoverRequestedAt: string | null
  } | null
}

// ============================================================
// CONSTANTS
// ============================================================

/** Глубина стека undo (RM-07) — кольцо, дроп старейшего при переполнении. */
const UNDO_DEPTH = 100

/** Порог мягкого предупреждения о производительности (RM-09). */
const PERF_THRESHOLD = 200

const BACKEND_NODE_TYPES: BackendNodeType[] = ['horizontal', 'vertical', 'checkpoint', 'condition']

/** Шаг каскада новых элементов (RM-02) — чтобы добавленные подряд не наслаивались точно. */
const CASCADE_STEP = 36

// ============================================================
// HELPERS
// ============================================================

/**
 * Клиентский id новых элементов. crypto.randomUUID() доступен только в secure
 * context (https или localhost) — по http с LAN/туннель-origin он undefined,
 * поэтому фолбэк на getRandomValues (UUID v4), затем на Math.random. Schema
 * `id String @id` принимает любую строку — сервер делает идемпотентный upsert (RM-06).
 */
export function createId(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined

  if (typeof c?.randomUUID === 'function') return c.randomUUID()

  if (typeof c?.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16))

    b[6] = (b[6] & 0x0f) | 0x40
    b[8] = (b[8] & 0x3f) | 0x80

    const h = Array.from(b, x => x.toString(16).padStart(2, '0'))

    return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`
  }

  return `id-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`
}

const initialState: RoadmapState = {
  nodes: [],
  edges: [],
  areas: [],
  notes: [],
  selectedId: null,
  selectedKind: null,
  dirty: { nodes: {}, edges: {}, areas: {}, notes: {} },
  deleted: { nodeIds: {}, edgeIds: {}, areaIds: {}, noteIds: {} },
  past: [],
  future: [],
  perfWarningShown: false,
  saveStatus: 'idle',
  mode: 'view',
  lock: null
}

/** Сериализуемый снимок структуры/атрибутов (без selection/viewport). */
function snapshot(state: RoadmapState): CanvasSnapshot {
  return {
    nodes: JSON.parse(JSON.stringify(state.nodes)),
    edges: JSON.parse(JSON.stringify(state.edges)),
    areas: JSON.parse(JSON.stringify(state.areas)),
    notes: JSON.parse(JSON.stringify(state.notes))
  }
}

/** Push снимок в past (кольцо глубиной 100) ПЕРЕД мутацией и очистить future. */
function pushHistory(state: RoadmapState): void {
  state.past.push(snapshot(state))

  if (state.past.length > UNDO_DEPTH) state.past.shift() // дроп старейшего

  state.future = []
}

/**
 * Согласовать dirty/deleted после восстановления снимка (undo/redo, CR-02).
 *
 * Восстановление среза НЕ помечает элементы dirty, поэтому автосохранение их не видит
 * → данные теряются. Здесь: всё, что есть ПОСЛЕ восстановления, помечаем dirty (upsert),
 * а всё, что было ДО, но исчезло (было добавлено и откатилось), помечаем deleted (чтобы
 * сервер удалил). Восстановление ранее удалённого элемента снимает его из deleted.
 *
 * @param before — id-наборы по 4 сущностям в состоянии ДО восстановления.
 */
function reconcileDirtyAfterRestore(
  state: RoadmapState,
  before: { nodes: string[]; edges: string[]; areas: string[]; notes: string[] }
): void {
  const afterNodes = new Set(state.nodes.map(n => n.id))
  const afterEdges = new Set(state.edges.map(e => e.id))
  const afterAreas = new Set(state.areas.map(a => a.id))
  const afterNotes = new Set(state.notes.map(n => n.id))

  // Всё восстановленное → dirty (upsert при следующем флаше). Снять из deleted, если было.
  for (const n of state.nodes) {
    state.dirty.nodes[n.id] = true
    delete state.deleted.nodeIds[n.id]
  }

  for (const e of state.edges) {
    state.dirty.edges[e.id] = true
    delete state.deleted.edgeIds[e.id]
  }

  for (const a of state.areas) {
    state.dirty.areas[a.id] = true
    delete state.deleted.areaIds[a.id]
  }

  for (const n of state.notes) {
    state.dirty.notes[n.id] = true
    delete state.deleted.noteIds[n.id]
  }

  // Было до восстановления, но исчезло → deleted (сервер удалит). Снять из dirty.
  for (const id of before.nodes) {
    if (!afterNodes.has(id)) {
      state.deleted.nodeIds[id] = true
      delete state.dirty.nodes[id]
    }
  }

  for (const id of before.edges) {
    if (!afterEdges.has(id)) {
      state.deleted.edgeIds[id] = true
      delete state.dirty.edges[id]
    }
  }

  for (const id of before.areas) {
    if (!afterAreas.has(id)) {
      state.deleted.areaIds[id] = true
      delete state.dirty.areas[id]
    }
  }

  for (const id of before.notes) {
    if (!afterNotes.has(id)) {
      state.deleted.noteIds[id] = true
      delete state.dirty.notes[id]
    }
  }
}

/**
 * Заливка содержимого канвы в state: ноды/связи/области/заметки + сброс
 * выделения, dirty/deleted, undo-истории и saveStatus. НЕ трогает mode/lock —
 * это решает вызывающий reducer (hydrate сбрасывает сессию, hydrateCanvas — нет).
 */
function hydrateContent(state: RoadmapState, payload: CanvasSnapshot) {
  state.nodes = payload.nodes
  state.edges = payload.edges
  state.areas = payload.areas
  state.notes = payload.notes
  state.selectedId = null
  state.selectedKind = null
  state.dirty = { nodes: {}, edges: {}, areas: {}, notes: {} }
  state.deleted = { nodeIds: {}, edgeIds: {}, areaIds: {}, noteIds: {} }
  state.past = []
  state.future = []
  state.perfWarningShown = false
  state.saveStatus = 'idle'
}

/** Дефолтная data для новой ноды по client-типу. */
function defaultNodeData(type: BackendNodeType): RoadmapNodeData {
  if (type === 'vertical') return { type: 'vertical', title: '', items: [] }
  if (type === 'checkpoint') return { type: 'checkpoint', label: '' }
  if (type === 'condition') return { type: 'condition', label: '', orientation: 'horizontal' }

  return { type: 'horizontal', title: '' }
}

// ============================================================
// SLICE
// ============================================================

const slice = createSlice({
  name: 'roadmap',
  initialState,
  reducers: {
    /** Заливка начального снимка канвы (из RSC props / GET). Сбрасывает сессию. */
    hydrate(state, action: PayloadAction<CanvasSnapshot>) {
      hydrateContent(state, action.payload)
      // Сброс сессии лока: после заливки нового снимка карта по умолчанию в просмотре (LOCK-01).
      // ТОЛЬКО для первичного mount-снимка — рефетч канвы использует hydrateCanvas (см. ниже).
      state.mode = 'view'
      state.lock = null
    },

    /**
     * Рефетч содержимого канвы (на «Обновить» / автообновлении), НЕ трогает mode/lock.
     * Заменяет только содержимое и сбрасывает dirty/deleted/undo (просмотрщик не держит
     * несохранённых правок), но сохраняет снимок лока — иначе индикатор «Редактирует: …»
     * мигнул бы на «Редактировать» до следующего status-poll (~10с). См. 11-UI-SPEC.
     */
    hydrateCanvas(state, action: PayloadAction<CanvasSnapshot>) {
      hydrateContent(state, action.payload)
    },

    /** React Flow контролируемый поток: применить изменения нод + пометить dirty/deleted. */
    onNodesChange(state, action: PayloadAction<NodeChange[]>) {
      state.nodes = applyNodeChanges(action.payload, state.nodes as unknown as RFNode[]) as unknown as RoadmapNode[]

      for (const ch of action.payload) {
        if (ch.type === 'remove') {
          state.deleted.nodeIds[ch.id] = true
          delete state.dirty.nodes[ch.id]
        } else if ('id' in ch) {
          state.dirty.nodes[ch.id] = true
        }
      }
    },

    /** React Flow контролируемый поток: применить изменения связей + пометить dirty/deleted. */
    onEdgesChange(state, action: PayloadAction<EdgeChange[]>) {
      state.edges = applyEdgeChanges(action.payload, state.edges as unknown as RFEdge[]) as unknown as RoadmapEdge[]

      for (const ch of action.payload) {
        if (ch.type === 'remove') {
          state.deleted.edgeIds[ch.id] = true
          delete state.dirty.edges[ch.id]
        } else if ('id' in ch) {
          state.dirty.edges[ch.id] = true
        }
      }
    },

    /** Новая связь от ручки (RM-03). sourceHandle → fromItemId (пункт верт. блока). */
    onConnect(state, action: PayloadAction<Connection>) {
      pushHistory(state)

      const connection = action.payload
      const id = createId()

      const next = addEdge({ ...connection, id }, state.edges as unknown as RFEdge[]) as unknown as RoadmapEdge[]

      // addEdge мог сгенерить свой id, если наш не сохранился — берём фактический новый.
      state.edges = next.map(e => ({ ...e, fromItemId: e.fromItemId ?? e.sourceHandle ?? null }))

      const created = state.edges.find(e => e.source === connection.source && e.target === connection.target)

      if (created) state.dirty.edges[created.id] = true
    },

    /** Создать ноду в позиции (D-02). Генерит id, markDirty, порог 201 (RM-09). */
    addRoadmapNode(
      state,
      action: PayloadAction<{
        type: ClientNodeType
        position: { x: number; y: number }
        orientation?: 'horizontal' | 'vertical'
      }>
    ) {
      pushHistory(state)

      const { type } = action.payload

      // Каскад: элементы, добавленные подряд из одной точки (центр окна), смещаются
      // по диагонали, чтобы не наслаиваться точно друг на друга (иначе ручки связи
      // верхней ноды перекрыты нижней и связь невозможно протянуть; RM-02/RM-03).
      const total = state.nodes.length + state.areas.length + state.notes.length
      const offset = (total % 8) * CASCADE_STEP
      const position = { x: action.payload.position.x + offset, y: action.payload.position.y + offset }

      if (type === 'area') {
        const id = createId()

        state.areas.push({ id, position, width: 300, height: 200, label: null, color: null })
        state.dirty.areas[id] = true

        return
      }

      if (type === 'note') {
        const id = createId()

        state.notes.push({ id, position, width: 200, height: 160, text: null, color: null })
        state.dirty.notes[id] = true

        return
      }

      const backendType: BackendNodeType = BACKEND_NODE_TYPES.includes(type) ? type : 'horizontal'
      const id = createId()
      const before = state.nodes.length
      const data = defaultNodeData(backendType)

      // orientation несёт только ромб-условие — для прочих типов payload.orientation игнорируется.
      if (backendType === 'condition' && action.payload.orientation && data.type === 'condition') {
        data.orientation = action.payload.orientation
      }

      state.nodes.push({ id, type: backendType, position, data })
      state.dirty.nodes[id] = true

      // Порог 200→201 один раз за сессию (RM-09).
      if (before === PERF_THRESHOLD && state.nodes.length === PERF_THRESHOLD + 1 && !state.perfWarningShown) {
        state.perfWarningShown = true
      }
    },

    /** Правка data ноды (панель свойств RM-04). */
    updateNodeData(state, action: PayloadAction<{ id: string; data: RoadmapNodeData }>) {
      const node = state.nodes.find(n => n.id === action.payload.id)

      if (!node) return

      pushHistory(state)
      node.data = action.payload.data
      state.dirty.nodes[node.id] = true
    },

    /** Правка области-подложки (label/color, панель свойств RM-04, D-04). */
    updateArea(state, action: PayloadAction<{ id: string; patch: Partial<Pick<RoadmapArea, 'label' | 'color'>> }>) {
      const area = state.areas.find(a => a.id === action.payload.id)

      if (!area) return

      pushHistory(state)
      Object.assign(area, action.payload.patch)
      state.dirty.areas[area.id] = true
    },

    /** Правка заметки (text/color, панель свойств RM-04). */
    updateNote(state, action: PayloadAction<{ id: string; patch: Partial<Pick<RoadmapNote, 'text' | 'color'>> }>) {
      const note = state.notes.find(n => n.id === action.payload.id)

      if (!note) return

      pushHistory(state)
      Object.assign(note, action.payload.patch)
      state.dirty.notes[note.id] = true
    },

    /**
     * Контролируемый поток для area/note (React Flow ноды типов 'area'/'note').
     * Применяет position/dimensions/remove напрямую (отдельные таблицы, НЕ state.nodes).
     */
    onAuxNodesChange(
      state,
      action: PayloadAction<
        {
          kind: 'area' | 'note'
          id: string
          position?: { x: number; y: number }
          width?: number
          height?: number
          remove?: boolean
        }[]
      >
    ) {
      for (const ch of action.payload) {
        const list = ch.kind === 'area' ? state.areas : state.notes
        const dirtyKey = ch.kind === 'area' ? 'areas' : 'notes'
        const deletedKey = ch.kind === 'area' ? 'areaIds' : 'noteIds'

        if (ch.remove) {
          if (ch.kind === 'area') state.areas = state.areas.filter(a => a.id !== ch.id)
          else state.notes = state.notes.filter(n => n.id !== ch.id)

          state.deleted[deletedKey][ch.id] = true
          delete state.dirty[dirtyKey][ch.id]
          continue
        }

        const item = list.find(el => el.id === ch.id)

        if (!item) continue

        if (ch.position) item.position = ch.position
        if (ch.width !== undefined) item.width = ch.width
        if (ch.height !== undefined) item.height = ch.height
        state.dirty[dirtyKey][ch.id] = true
      }
    },

    /** Реверс направления связи (RM-03): swap source↔target, fromItemId=null. */
    reverseEdge(state, action: PayloadAction<string>) {
      const edge = state.edges.find(e => e.id === action.payload)

      if (!edge) return

      pushHistory(state)

      const { source, target, sourceHandle, targetHandle } = edge

      edge.source = target
      edge.target = source
      edge.sourceHandle = targetHandle ?? null
      edge.targetHandle = sourceHandle ?? null
      // item-handle есть только на source-стороне верт. блока — при реверсе обнуляем.
      edge.fromItemId = null

      state.dirty.edges[edge.id] = true
    },

    /** Выделить элемент. */
    select(state, action: PayloadAction<{ id: string; kind: RoadmapState['selectedKind'] } | null>) {
      state.selectedId = action.payload?.id ?? null
      state.selectedKind = action.payload?.kind ?? null
    },

    /** Удалить выделенный элемент/связь (RM-04, кнопка в панели свойств). */
    deleteSelected(state) {
      const { selectedId, selectedKind } = state

      if (!selectedId || !selectedKind) return

      pushHistory(state)

      if (selectedKind === 'node') {
        state.nodes = state.nodes.filter(n => n.id !== selectedId)
        state.deleted.nodeIds[selectedId] = true
        delete state.dirty.nodes[selectedId]

        // Каскад: удалить связи, инцидентные ноде (нет FK на item — чистим вручную).
        for (const e of state.edges) {
          if (e.source === selectedId || e.target === selectedId) {
            state.deleted.edgeIds[e.id] = true
            delete state.dirty.edges[e.id]
          }
        }

        state.edges = state.edges.filter(e => e.source !== selectedId && e.target !== selectedId)
      } else if (selectedKind === 'edge') {
        state.edges = state.edges.filter(e => e.id !== selectedId)
        state.deleted.edgeIds[selectedId] = true
        delete state.dirty.edges[selectedId]
      } else if (selectedKind === 'area') {
        state.areas = state.areas.filter(a => a.id !== selectedId)
        state.deleted.areaIds[selectedId] = true
        delete state.dirty.areas[selectedId]
      } else if (selectedKind === 'note') {
        state.notes = state.notes.filter(n => n.id !== selectedId)
        state.deleted.noteIds[selectedId] = true
        delete state.dirty.notes[selectedId]
      }

      state.selectedId = null
      state.selectedKind = null
    },

    /** Undo (RM-07): вытолкнуть past → future, восстановить срез + согласовать dirty (CR-02). */
    undo(state) {
      const prev = state.past.pop()

      if (!prev) return

      const before = {
        nodes: state.nodes.map(n => n.id),
        edges: state.edges.map(e => e.id),
        areas: state.areas.map(a => a.id),
        notes: state.notes.map(n => n.id)
      }

      state.future.push(snapshot(state))
      state.nodes = prev.nodes
      state.edges = prev.edges
      state.areas = prev.areas
      state.notes = prev.notes

      // CR-02: иначе восстановленные элементы читаются как clean → autosave молчит → потеря данных.
      reconcileDirtyAfterRestore(state, before)
    },

    /** Redo (RM-07): вытолкнуть future → past, восстановить срез + согласовать dirty (CR-02). */
    redo(state) {
      const next = state.future.pop()

      if (!next) return

      const before = {
        nodes: state.nodes.map(n => n.id),
        edges: state.edges.map(e => e.id),
        areas: state.areas.map(a => a.id),
        notes: state.notes.map(n => n.id)
      }

      state.past.push(snapshot(state))
      state.nodes = next.nodes
      state.edges = next.edges
      state.areas = next.areas
      state.notes = next.notes

      // CR-02: симметрично undo — повторённое изменение должно сохраниться.
      reconcileDirtyAfterRestore(state, before)
    },

    /** Сброс undo-сессии при выходе из edit-режима (RM-07). */
    resetEditSession(state) {
      state.past = []
      state.future = []
    },

    /** Индикатор сохранения (RM-06). */
    setSaveStatus(state, action: PayloadAction<RoadmapState['saveStatus']>) {
      state.saveStatus = action.payload
    },

    /** Переключить режим view/edit (LOCK-01). */
    setMode(state, action: PayloadAction<RoadmapState['mode']>) {
      state.mode = action.payload
    },

    /** Записать снимок лока (из status-poll / heartbeat-ответа). null = лок свободен (LOCK-04/08). */
    setLock(state, action: PayloadAction<RoadmapState['lock']>) {
      state.lock = action.payload
    },

    /** Очистить dirty/deleted после успешного сейва (RM-06). */
    clearDirty(state) {
      state.dirty = { nodes: {}, edges: {}, areas: {}, notes: {} }
      state.deleted = { nodeIds: {}, edgeIds: {}, areaIds: {}, noteIds: {} }
    }
  }
})

export const {
  hydrate,
  hydrateCanvas,
  onNodesChange,
  onEdgesChange,
  onConnect,
  addRoadmapNode,
  updateNodeData,
  updateArea,
  updateNote,
  onAuxNodesChange,
  reverseEdge,
  select,
  deleteSelected,
  undo,
  redo,
  resetEditSession,
  setSaveStatus,
  setMode,
  setLock,
  clearDirty
} = slice.actions

export default slice.reducer
